import objectToFormData from 'object-to-formdata';
import * as qs from 'querystring';
import * as Url from 'url';
import CryptoJS from 'crypto-js';
import { getVendorStorage, removeVendorStorage } from '@mpc/common/src/utils/vendorStorage';
import { isPlainObject, type } from './jqUtil';
import showAlert from './showAlert';
import postDownload from './postDownload';
import { isNil, isBoolean } from 'lodash';
import { isDevOrTest } from './datracker';
import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { $sType } from '#utils';
import { axiosErrorHandle } from '#utils/axiosErrorHandle';
import throttlingPromise from '#utils/throttlingPromise';
import ServiceError from './serviceError';
import { skipToLogin } from './ajax';
import FetchError from './fetchError';
import camelCaseKeys, { Options as CamelCaseOptions } from 'camelcase-keys';
import { PartnershipRequestInvoker } from '@purchase/supplier';

interface AlertOptions {
  pending?: string | { type: string; msg: string };
  fulfilled?: { type?: string; msg?: string };
  rejected?: { type?: string; msg?: string };
}

export type RequestOptions = AxiosRequestConfig & {
  body?: any;
  dataType?: 'json' | 'text' | 'blob';
  alert?: AlertOptions;
  withUUID?: boolean;
  useFormData?: boolean;
  silent?: boolean;
  openapi?: boolean;
  version?: string;
  errorAlertDuration?: number;
  fileName?: string;
  cancelRef?: { current: () => void };
  camelCase?: boolean | CamelCaseOptions;
};

export const adaptMpcExternalOuterPath = (url: string) => {
  if ($sType === 'outer') {
    if (url.startsWith('/api/outsource/')) {
      url = url
        .replace('/api/outsource/', '/api/babel/mpc/')
        .replace('/java_proxy', '');
    }

    if (url.includes('/cooperation/invoke') && !url.includes('/solar')) {
      url = url.replace('/cooperation/invoke', '/solar/cooperation/invoke');
    }
  }
  return url;
};

export const clearNull = (body: any, convertEmptyArray = false): any => {
  let temp: any;

  if (Array.isArray(body)) {
    const newBody: any[] = [];

    if (body.length) {
      body
        .filter((item) => item !== null)
        .forEach((item) => {
          temp = clearNull(item, convertEmptyArray);

          if (temp !== null) {
            newBody.push(temp);
          }
        });
    }

    return newBody.length ? newBody : convertEmptyArray ? [''] : null;
  } else if (isPlainObject(body)) {
    const newBody: { [key: string]: any } = {};

    for (let key in body) {
      if (body[key] === null) {
        continue;
      }

      temp = clearNull(body[key], convertEmptyArray);

      if (temp !== null) {
        newBody[key] = temp;
      }
    }

    return newBody;
  } else {
    return body;
  }
};

// 针对返回的结果单独处理['']约定的数据
const convertResult = (body: any): any => {
  if (Array.isArray(body)) {
    if (body.length === 1 && body[0] === '') {
      return [];
    }

    body.forEach((item, index) => {
      body[index] = convertResult(item);
    });

    return body;
  } else if (isPlainObject(body)) {
    for (let key in body) {
      body[key] = convertResult(body[key]);
    }

    return body;
  } else {
    return body;
  }
};

// 幂等相关
const REQUEST_UUID_MAP = new Map<string, string>();

const createUUID = (): string => {
  let dt = new Date().getTime();
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (dt + Math.random() * 16) % 16 | 0;
    dt = Math.floor(dt / 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
  return uuid;
};

const setRequestUUID = (ajaxParams: RequestOptions): string => {
  const { url = '', method = '', body } = ajaxParams;
  const bodyJson = JSON.stringify(body || {});
  const bodyJsonHash = CryptoJS.SHA1(bodyJson).toString();
  const uuidKey = `${method}:${url}@${bodyJsonHash}`;

  if (!REQUEST_UUID_MAP.get(uuidKey)) {
    REQUEST_UUID_MAP.set(uuidKey, createUUID());
  }

  const uuidValue = REQUEST_UUID_MAP.get(uuidKey)!;

  if (!ajaxParams.headers) {
    ajaxParams.headers = {};
  }
  ajaxParams.headers['X-MPC-UUID'] = uuidValue;

  return uuidKey;
};

const removeRequestUUID = (uuidKey: string): void => {
  REQUEST_UUID_MAP.delete(uuidKey);
};

function bindAxios(options: RequestOptions = {}): Promise<any> {
  let bodyTemp = options.body;
  options = Object.assign(
    {
      method: 'GET',
      headers: {},
      body: {},
      dataType:
        options?.url && options?.url?.match(/\.(html|css)/) ? 'text' : 'json',
      alert: {},
    },
    options,
    { body: bodyTemp }
  );

  const { withUUID = true, camelCase = false } = options;
  let { alert, body, method = 'GET', silent } = options;

  if (alert) {
    if (isPlainObject(alert)) {
      if (!('rejected' in alert)) {
        alert.rejected = {};
      }

      const { pending, fulfilled, rejected } = alert;

      if (pending !== undefined) {
        if (type(pending) === 'string') {
          alert.pending = { type: 'info', msg: pending };
        } else if (!isPlainObject(pending)) {
          delete alert.pending;
        }
      }

      if (fulfilled !== undefined) {
        if (type(fulfilled) === 'boolean' && fulfilled) {
          alert.fulfilled = { type: 'success' };
        } else if (type(fulfilled) === 'string') {
          alert.fulfilled = { type: 'success', msg: fulfilled };
        } else if (!isPlainObject(fulfilled)) {
          delete alert.fulfilled;
        }
      }

      if (rejected !== undefined) {
        if (type(rejected) === 'boolean' && rejected) {
          alert.rejected = { type: 'danger' };
        } else if (type(rejected) === 'string') {
          alert.rejected = { type: 'danger', msg: rejected };
        } else if (!isPlainObject(rejected)) {
          delete alert.rejected;
        }
      }
    } else if (type(alert) === 'boolean') {
      alert = { rejected: { type: 'danger' } };
    } else if (type(alert) === 'string') {
      alert = { rejected: { type: 'danger', msg: alert } };
    }
  }

  let uuidKey;
  if (withUUID && ['POST', 'PUT', 'DELETE'].includes(method)) {
    uuidKey = setRequestUUID(options);
  }

  if (method === 'GET') {
    const urlObject = Url.parse(options?.url || '');

    urlObject.query = Object.assign(urlObject.query || {}, body);
    options.url = Url.format(urlObject);
    delete options.body;
  } else {
    const contentType = options.headers['Content-Type'];

    if (options.useFormData && options.body) {
      options.body = objectToFormData(options.body);
    } else if (isPlainObject(body)) {
      if (!contentType || contentType === 'json') {
        options.headers['Content-Type'] = 'application/json; charset=utf-8';
        options.body = JSON.stringify(body);
      } else if (contentType === 'form') {
        options.headers['Content-Type'] =
          'application/x-www-form-urlencoded; charset=utf-8';
        options.body = qs.stringify(body);
      }
    }
  }

  options.headers['X-Requested-With'] = 'XMLHttpRequest';

  if (isDevOrTest()) {
    const querystring = location.search ? location.search.slice(1) : '';
    const querystringInfo = qs.parse(querystring);
    const { id = '' } = querystringInfo;

    if (id) {
      options.headers['User'] = id;
    }
  }

  let { openapi, url, dataType, version = 'v1', ...otherOptions } = options;
  url = openapi ? url : `/api/${version}/` + url;

  if ($sType === 'outer') {
    url = adaptMpcExternalOuterPath(url);
  }

  if (otherOptions?.cancelRef) {
    otherOptions.cancelToken = new axios.CancelToken((cancel) => {
      otherOptions.cancelRef.current = cancel;
    });
  }

  let outerHeaders = {};

  if ($sType === 'outer') {
    const { vendorId, tenantCode } = getVendorStorage();
    if (vendorId) {
      outerHeaders['Vendor-Id'] = vendorId;
    }
    if (tenantCode) {
      outerHeaders['Tenant-Code'] = tenantCode;
    }
  }

  let resultPromise = axios({
    ...otherOptions,
    url,
    method,
    headers: {
      // 添加前端版本号
      ['x-fe-version-tag']: window.$versionTag || 'dev',
      ...outerHeaders,
      ...options.headers,
    },
    data: method !== 'GET' ? options.body : null,
    params: method === 'GET' ? options.body : null,
    responseType: dataType === 'blob' ? 'blob' : 'json',
    withCredentials: true,
  });

  resultPromise.catch((e: AxiosError) => {
    if (e.response && e.response.status === 401) {
      if (location.href.includes('#/reset')) return;
      skipToLogin();
      return;
    }

    if (options.toRubby) return;

    if (e.response && e.response.status === 403) {
      window.location.href = '/';
      return;
    }

    if (e.message.indexOf('Failed to fetch') > -1) {
      const errMessage = JSON.stringify({
        ...options,
        url,
        name: e.name,
        message: e.message,
      });
      throw new FetchError(errMessage);
    } else if (alert && alert.rejected) {
      if (!alert.rejected.msg) {
        alert.rejected.msg = e.message;
      }
    }

    showAlert({
      type: 'error',
      message: e.message || '服务器错误',
      hideDelay: options?.errorAlertDuration || 3000,
    });

    throw new ServiceError(
      typeof e === 'string' ? e : e.message || '服务器错误'
    );
  });

  return resultPromise.then((response: AxiosResponse) => {
    const { data, headers } = response;

    if (dataType === 'blob' && headers['content-disposition']) {
      if (method === 'POST') {
        return postDownload(response, options);
      } else {
        return data;
      }
    }

    let result = data;

    if (dataType === 'json') {
      result = convertResult(result);

      if (isNil(result.res_code) && !isNil(result.code)) {
        result.res_code = result.code;
      }

      if (isNil(result.res_code) && !isNil(result.status)) {
        result.res_code = result.status;
      }

      if (!result.res_msg && result.message) {
        result.res_msg = result.message;
      }

      if (result.res_code !== undefined) {
        if (
          result.res_code === -1 ||
          result.res_code === 4001 ||
          result.res_code === 401
        ) {
          removeVendorStorage();
          skipToLogin();
        } else if (result.res_code === 0 || result.res_code !== 1) {
          const errMessage = Array.isArray(result.res_msg)
            ? result.res_msg.join('<br/>')
            : typeof result.res_msg === 'string'
            ? result.res_msg
            : '';
          if (!silent) {
            if (axiosErrorHandle.handle) {
              axiosErrorHandle.handle?.(result, options);
            } else {
              showAlert({
                type: 'error',
                message: errMessage || '服务器错误',
                hideDelay: options?.errorAlertDuration || 3000,
              });
            }
          }
          let error = new ServiceError(errMessage);
          error.data = { ...result };
          throw error;
        } else if (result.res_code === 1) {
          // success
        } else {
          throw new ServiceError('服务器错误');
        }
      }
    }

    if (alert && alert.fulfilled) {
      if (!alert.fulfilled.msg) {
        alert.fulfilled.msg = result.msg;
      }
    }

    if (uuidKey) {
      removeRequestUUID(uuidKey);
    }

    if (camelCase) {
      const camelCaseOptions = isBoolean(camelCase) ? undefined : camelCase;

      result = camelCaseKeys(result, {
        deep: true,
        preserveConsecutiveUppercase: true,
        ...camelCaseOptions,
      });
    }

    return result;
  });
}

const throttlingAxios = throttlingPromise(bindAxios);

export default throttlingAxios;
